<template>
  <div v-if="meta" class="cms-pagination">
    <cms-rows-per-page
      :id="`${id}-rows-per-page`"
      :selected="meta.perPage"
      :options="perPageOptions"
      @changed="handlePerPageChange"
    />

    <div v-if="meta.lastPage > 1" class="cms-pagination-controls">
      <button
        class="cms-pagination-btn"
        :disabled="meta.currentPage === 1"
        @click="handlePageChange(meta.currentPage - 1)"
      >
        <v-icon icon="chevron-left" size="sm" />
      </button>

      <div class="cms-pagination-pages">
        <button
          v-if="pageNumbers[0] > 1"
          class="cms-pagination-page"
          :class="{ active: false }"
          @click="handlePageChange(1)"
        >
          1
        </button>
        <span v-if="pageNumbers[0] > 2" class="cms-pagination-ellipsis">...</span>

        <button
          v-for="pageNum in pageNumbers"
          :key="pageNum"
          class="cms-pagination-page"
          :class="{ active: meta.currentPage === pageNum }"
          @click="handlePageChange(pageNum)"
        >
          {{ pageNum }}
        </button>

        <span
          v-if="pageNumbers[pageNumbers.length - 1] < meta.lastPage - 1"
          class="cms-pagination-ellipsis"
        >...</span>

        <button
          v-if="pageNumbers[pageNumbers.length - 1] < meta.lastPage"
          class="cms-pagination-page"
          :class="{ active: false }"
          @click="handlePageChange(meta.lastPage)"
        >
          {{ meta.lastPage }}
        </button>
      </div>

      <button
        class="cms-pagination-btn"
        :disabled="meta.currentPage === meta.lastPage"
        @click="handlePageChange(meta.currentPage + 1)"
      >
        <v-icon icon="chevron-right" size="sm" />
      </button>
    </div>

    <div class="cms-pagination-info">
      <span class="cms-pagination-info-text">
        {{ $t('general.shared.tableShowing', {
          from: meta.from || 0,
          to: meta.to || 0,
          total: meta.total || 0
        }) }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import CmsRowsPerPage from './CmsRowsPerPage.vue';

interface PaginationMeta {
  currentPage: number;
  lastPage: number;
  perPage: number;
  from: number | null;
  to: number | null;
  total: number;
  path?: string;
}

interface Props {
  meta: PaginationMeta | null;
  perPageOptions?: number[];
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  perPageOptions: () => [10, 25, 50, 100],
  id: 'cms-pagination',
});

const emit = defineEmits<{
  (e: 'pageChanged', page: number): void;
  (e: 'perPageChanged', perPage: number): void;
}>();

const { t } = useI18n();

const maxPageLinks = 5;

const pageNumbers = computed(() => {
  if (!props.meta) return [];
  
  const pages = [];
  let startPage: number, endPage: number;
  const currentPage = props.meta.currentPage;
  const lastPage = props.meta.lastPage;

  if (lastPage <= maxPageLinks) {
    startPage = 1;
    endPage = lastPage;
  } else {
    const maxPagesBeforeCurrentPage = Math.floor(maxPageLinks / 2);
    const maxPagesAfterCurrentPage = Math.ceil(maxPageLinks / 2) - 1;

    if (currentPage <= maxPagesBeforeCurrentPage) {
      startPage = 1;
      endPage = maxPageLinks;
    } else if (currentPage + maxPagesAfterCurrentPage >= lastPage) {
      startPage = lastPage - maxPageLinks + 1;
      endPage = lastPage;
    } else {
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return pages;
});

const handlePageChange = (page: number) => {
  if (page >= 1 && page <= (props.meta?.lastPage || 1)) {
    emit('pageChanged', page);
  }
};

const handlePerPageChange = (perPage: number) => {
  if (props.meta && props.meta.perPage !== perPage) {
    emit('perPageChanged', perPage);
  }
};
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-pagination {
  display: flex;
  flex-direction: column;
  gap: $cms-spacing-md;
  padding: $cms-spacing-md 0;
  margin-top: $cms-spacing-lg;

  @media (min-width: $cms-breakpoint-mobile) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.cms-pagination-controls {
  display: flex;
  align-items: center;
  gap: $cms-spacing-xs;
  flex-wrap: wrap;
  justify-content: center;
}

.cms-pagination-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid $cms-border-color-dark;
  border-radius: $cms-radius-sm;
  background: $cms-background-white;
  color: $cms-text-primary;
  cursor: pointer;
  transition: all $cms-transition-base;
  font-size: $cms-font-size-sm;

  &:hover:not(:disabled) {
    background: $cms-background-light;
    border-color: $cms-primary-color;
    color: $cms-primary-color;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    @include cms-focus-ring;
  }
}

.cms-pagination-pages {
  display: flex;
  align-items: center;
  gap: $cms-spacing-xs;
  flex-wrap: wrap;
}

.cms-pagination-page {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 $cms-spacing-sm;
  border: 1px solid $cms-border-color-dark;
  border-radius: $cms-radius-sm;
  background: $cms-background-white;
  color: $cms-text-primary;
  cursor: pointer;
  transition: all $cms-transition-base;
  font-size: $cms-font-size-sm;
  font-weight: 500;

  &:hover:not(.active) {
    background: $cms-background-light;
    border-color: $cms-primary-color;
    color: $cms-primary-color;
  }

  &.active {
    background: $cms-primary-color;
    border-color: $cms-primary-color;
    color: white;
    font-weight: 600;
  }

  &:focus {
    @include cms-focus-ring;
  }
}

.cms-pagination-ellipsis {
  display: inline-flex;
  align-items: center;
  padding: 0 $cms-spacing-xs;
  color: $cms-text-secondary;
  font-size: $cms-font-size-sm;
}

.cms-pagination-info {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.cms-pagination-info-text {
  font-size: $cms-font-size-sm;
  color: $cms-text-secondary;
  white-space: nowrap;
}

@media (max-width: $cms-breakpoint-mobile) {
  .cms-pagination {
    align-items: stretch;
  }

  .cms-pagination-controls {
    justify-content: center;
  }

  .cms-pagination-info {
    justify-content: center;
  }
}
</style>

